/**
 * The networks the registration page offers: only those the reader may add a node to
 * (docs/PERMISSIONS.md § 5 Attachment, decided Q-4, app#397).
 *
 * A network whose "members may add nodes" setting is off takes a new node only from its owner or
 * an org Owner/Admin. The router's `RequireAttach` refuses everyone else at the approval. This file
 * keeps the page from offering such a network, and says which networks it did not list and why.
 *
 * ⚠ THE ROUTER IS THE GATE. This only removes a choice that will be refused. Where a fact is not
 * known (the role in an org, or a network's settings), the network STAYS listed: a missing fact
 * must never hide a network the reader may use. The approval then answers as before.
 *
 * Pure, so `registerNetworks.test.ts` checks it with `node --experimental-strip-types`.
 */
import { mayAddNodes, type OrgRole } from './orgRole.ts'
import type { RegisterOrg, RegisterSession } from './registerSession.ts'

/** What the database says about one network, read by id. */
export interface NetworkAttachFacts {
  ownerId: string | null
  /** Null or absent: the column is not there (before router migration 0230) or not read. */
  membersMayAddNodes: boolean | null
  /**
   * The reader owns a node in this network. Null or absent: not known.
   *
   * ⚠⚠ Such a network STAYS LISTED even when the reader may not add a node to it. The router asks
   * the attach rule only for a NEW node: replacing the reader's own offline node, or re-keying it,
   * adds nothing to the network, and the router lets a Member do it on a closed network
   * (router `TestTheApprovalDoesNotAskTheAttachRuleForAReplace`). Hiding the network would take
   * that path away from the page.
   */
  readerOwnsNode?: boolean | null
}

/** The networks of the reader's nodes, from `nodes` rows with their `node_networks` rows. */
export const networksOfNodes = (rows: { node_networks?: { network_id: string }[] | null }[]): Set<string> =>
  new Set(rows.flatMap(row => (row.node_networks ?? []).map(membership => membership.network_id)))

/** The page's view of the record: the networks it offers, and, per org, the names it left out. */
export interface AddableView {
  session: RegisterSession
  /** Org id → the names of the networks not listed, in the router's order. */
  hidden: Record<string, string[]>
}

/**
 * `session` with each org's networks cut to the ones `userId` may add a node to.
 *
 * `roles` holds the reader's role per org id. An org with no entry keeps every network, and so does
 * a network with no entry in `facts`, and a network in which the reader owns a node (or may: the
 * fact is not known), because the router lets them replace that node. ⚠ A null role is also kept: it means the page could not read
 * it, not that the reader has none (the router lists only the reader's own orgs).
 */
export const addableSession = (
  session: RegisterSession,
  facts: ReadonlyMap<string, NetworkAttachFacts>,
  roles: ReadonlyMap<string, OrgRole>,
  userId: string | null
): AddableView => {
  const hidden: Record<string, string[]> = {}
  if (!userId) return { session, hidden }
  let changed = false
  const orgs: RegisterOrg[] = session.orgs.map(org => {
    const role = roles.get(org.id)
    if (!role) return org
    const networks = org.networks.filter(network => {
      const fact = facts.get(network.id)
      if (!fact) return true
      if (mayAddNodes(role, fact.ownerId, userId, fact.membersMayAddNodes)) return true
      if (fact.readerOwnsNode !== false) return true
      ;(hidden[org.id] ??= []).push(network.name)
      return false
    })
    if (networks.length === org.networks.length) return org
    changed = true
    return { ...org, networks }
  })
  return changed ? { session: { ...session, orgs }, hidden } : { session, hidden }
}

/**
 * The line under the network selector that says which networks are not listed, and why. Null when
 * none is left out.
 */
export const hiddenNetworksLine = (names: readonly string[] | undefined): string | null => {
  if (!names?.length) return null
  if (names.length === 1)
    return `${names[0]} is not listed. Only its owner, or an owner or admin of the organization, can add nodes to it.`
  return `${names.join(', ')} are not listed. Only their owners, or an owner or admin of the organization, can add nodes to them.`
}

/**
 * The alert when the org has networks but the reader may add a node to none of them. Any member may
 * create a network and owns it (decided Q-3), so the remedy is to create one.
 */
export const noAddableNetworkLine = (orgName: string | null): string =>
  `You cannot add nodes to the networks in ${orgName ?? 'this organization'}. Create a network for this machine.`
